import {
  Button,
  createDataTableColumnHelper,
  DataTable,
  Link as UiLink,
  SearchField,
  Switch,
} from "@beweegbaas/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { adminActivitiesOverviewQueryOptions } from "#/modules/beweegactiviteitenreeks.overview/query.activities-overview";
import { TogglePublishButton } from "#/modules/beweegactiviteitenreeks.overview/toggle-publish-button";
import {
  getBeweegThemaTranslation,
  getLeerjaarComboTranslation,
  getLeerlijnTranslation,
} from "#/modules/util.i18n/enum-mapping";
import {
  ADMIN_ACTIVITIES_SEARCH_DEFAULTS,
  adminActivitiesSearchSchema,
} from "#/modules/beweegactiviteitenreeks.overview/schema.search";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { FilterBar, type FilterBarFilter } from "#/components/filter-bar";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";
import { Beweegthema, LeerjaarCombo, Leerlijn } from "#/schema.nswag";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/activities/")({
  validateSearch: adminActivitiesSearchSchema,
  // Keeps default filter/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(ADMIN_ACTIVITIES_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; the query cache owns freshness (a deps change
  // still re-runs the loader for the new key).
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(adminActivitiesOverviewQueryOptions(deps)),
  component: BeweegactiviteitenreeksenAdminOverview,
  errorComponent: ActivitiesError,
});

function ActivitiesError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type ActivityRow = components["schemas"]["BeweegactiviteitenreeksAdminOverviewModel"];

const helper = createDataTableColumnHelper<ActivityRow>();

const columns = helper.columns([
  helper.accessor("name", {
    header: () => m["common.fields.name"](),
    meta: { rowTarget: true },
    cell: ({ row, getValue }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/admin/activities/$id/edit"
        params={(prev: Record<string, string>) => ({ ...prev, id: row.original.id })}
      >
        {getValue()}
      </UiLink>
    ),
  }),
  helper.accessor("schoolName", {
    header: () => m["common.fields.school"](),
    cell: ({ getValue }) => getValue() || m["common.labels.default_school"](),
  }),
  helper.accessor("leerlijn", {
    header: () => m["beweegactiviteitenreeks.table.leerlijn"](),
    cell: ({ getValue }) => getLeerlijnTranslation(getValue()),
  }),
  helper.accessor("beweegthema", {
    header: () => m["beweegactiviteitenreeks.table.beweegthema"](),
    cell: ({ getValue }) => getBeweegThemaTranslation(getValue()),
  }),
  helper.accessor("leerjaarCombo", {
    header: () => m["common.leerjaar"](),
    cell: ({ getValue }) =>
      getValue()
        .map((combo) => getLeerjaarComboTranslation(combo))
        .join(", "),
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => (
      <>
        {row.original.canTogglePublished && (
          <TogglePublishButton reeksId={row.original.id} isPublished={row.original.isPublished} />
        )}
      </>
    ),
  }),
]);

function BeweegactiviteitenreeksenAdminOverview() {
  const loaderData = Route.useLoaderData();
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, Route.useNavigate());

  // Reads live in TanStack Query so the publish-toggle's invalidation refreshes this list too;
  // `loaderData` is only the render fallback if the cache entry was GC'd.
  const overviewQuery = useQuery({
    ...adminActivitiesOverviewQueryOptions(search),
    placeholderData: keepPreviousData,
  });
  const { activities, totalPages, totalCount } = overviewQuery.data ?? loaderData;

  const filterBarFilters: FilterBarFilter[] = [
    {
      key: "leerlijn",
      label: m["beweegactiviteitenreeks.table.leerlijn"](),
      options: Object.values(Leerlijn).map((value) => ({
        value,
        label: getLeerlijnTranslation(value),
      })),
      selectedValues: search.leerlijn,
      onValuesChange: (values) => overview.setFilter({ leerlijn: values }),
      operator: search.leerlijnOperator,
      onOperatorChange: (operator) => overview.setFilter({ leerlijnOperator: operator }),
    },
    {
      key: "beweegthema",
      label: m["beweegactiviteitenreeks.table.beweegthema"](),
      options: Object.values(Beweegthema).map((value) => ({
        value,
        label: getBeweegThemaTranslation(value),
      })),
      selectedValues: search.beweegthema,
      onValuesChange: (values) => overview.setFilter({ beweegthema: values }),
      operator: search.beweegthemaOperator,
      onOperatorChange: (operator) => overview.setFilter({ beweegthemaOperator: operator }),
    },
    {
      key: "leerjaarCombo",
      label: m["common.leerjaar"](),
      options: Object.values(LeerjaarCombo)
        .filter((value) => value !== LeerjaarCombo.None)
        .map((value) => ({ value, label: getLeerjaarComboTranslation(value) })),
      selectedValues: search.leerjaarCombo,
      onValuesChange: (values) => overview.setFilter({ leerjaarCombo: values }),
      operator: search.leerjaarComboOperator,
      onOperatorChange: (operator) => overview.setFilter({ leerjaarComboOperator: operator }),
    },
    {
      key: "isPublished",
      label: "Gepubliceerd",
      options: [
        { value: "true", label: "Ja" },
        { value: "false", label: "Nee" },
      ],
      selectedValues: search.isPublished,
      onValuesChange: (values) => overview.setFilter({ isPublished: values }),
    },
  ];

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField className="w-auto grow md:max-w-xs" {...overview.searchFieldProps} />
        </Page.Header.SearchField>
        <Page.Header.Section className="gap-3">
          <FilterBar
            filters={filterBarFilters}
            onClearAll={() =>
              overview.setFilter({
                leerlijn: [],
                leerlijnOperator: "in",
                beweegthema: [],
                beweegthemaOperator: "in",
                leerjaarCombo: [],
                leerjaarComboOperator: "in",
                isPublished: [],
              })
            }
          />
          <Switch
            id="showSchoolActivities"
            name={"showSchoolActivities"}
            checked={search.includeSchools}
            onCheckedChange={(checked) => overview.setFilter({ includeSchools: checked })}
          />
          <label htmlFor="showSchoolActivities" className="text-sm">
            Toon school activiteiten
          </label>
        </Page.Header.Section>

        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/admin/activities/add"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              <IconMaterialSymbolsAdd />
              {m["common.actions.add"]()}
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label={m["nav.beweegactiviteiten"]()}
          columns={columns}
          data={activities}
          getRowId={(activity) => activity.id.toString()}
          empty={m["admin.geen_activiteiten_gevonden"]()}
          getRowClassName={(activity) =>
            !activity.isPublished ? "text-muted-foreground line-through" : undefined
          }
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"mb-6 border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
